<script setup>
import { ref, computed, onMounted, nextTick, onBeforeUnmount } from "vue";
import { useGroupStore } from "@/stores/useGroupStore";
import { useUserStore } from "@/stores/useUserStore";
import * as groupService from "@/services/groupService";
import CustomTextarea from "../Generics/CustomTextarea.vue";
import { toTitleCase } from "@/utils/stringUtils";

import { useDraggableModal } from "@/composables/useDraggableModal";

const groupStore = useGroupStore();
const emit = defineEmits(["close", "refresh"]);

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

const props = defineProps({
  initialName: {
    type: String,
    default: "",
  },
});

// Estado do formulário
const formData = ref({
  groupName: "",
  description: "",
  // Hierarquia: "Turma A" dentro de "Alunos". Conceder permissão ao pai alcança
  // quem está nos filhos, sem repetir a pessoa em cada grupo.
  parentGroupId: null,
});

// Candidatos a grupo pai. Lista carregada à parte (GET /groups, já recortado
// pelo círculo no backend): `groupStore.groups` guarda só a PÁGINA aberta na
// tela de grupos, então usá-lo deixava de fora os grupos das outras páginas.
const allGroups = ref([]);


// Candidato a pai: grupo do círculo, sem papel de sistema. Fora:
//  - o "Todos" do círculo (isCircleDefault): já alcança todo mundo, ser pai
//    não acrescenta nada. "Alunos" e "Professores" (também do sistema) FICAM:
//    "Alunos" é o pai natural de uma turma criada à mão;
//  - os grupos da plataforma, criados pelo Sys (o "Todos" global do acervo):
//    GET /groups os devolve a todos, mas o backend recusa pai fora do círculo
//    ("O grupo pai precisa ser do seu círculo"), então a opção só daria erro.
//    Para o próprio Sys eles são do círculo dele e continuam na lista.
function isParentCandidate(g) {
  if (g.isCircleDefault ?? g.IsCircleDefault) return false;
  const creator = String(g.createdByUserName ?? g.CreatedByUserName ?? "").trim().toLowerCase();
  const logged = String(useUserStore().currentUser?.userName ?? "").trim().toLowerCase();
  if (creator === "sys" && logged !== "sys") return false;
  return true;
}
const parentGroupOptions = computed(() =>
  allGroups.value
    .filter(isParentCandidate)
    .map((g) => ({
      id: g.groupId ?? g.GroupId,
      name: g.groupName ?? g.GroupName,
    }))
    .filter((g) => g.id != null)
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
);

async function loadParentGroupOptions() {
  try {
    const response = await groupService.fetchGroups();
    allGroups.value = response.data ?? [];
  } catch (error) {
    console.warn(
      "[AddGroupForm] Falha ao carregar os grupos para o seletor de pai:",
      error.response?.data?.message || error.message
    );
    allGroups.value = [];
  }
}

const errors = ref({});
const isSubmitting = ref(false);

// ====================================================================
// ✅ TOAST PADRÃO ADDUSERGROUPFORM.VUE (3000ms) 🚀
// (Copiado de AddActivityForm.vue para consistência)
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error"); // "error" | "success"

// Ao criar com sucesso, esconde só o painel do modal; o toast (num Teleport
// próprio) segue visível sozinho até o emit("close") desmontar o componente.
const hideBackdropForSuccess = ref(false);

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null; // Limpa a referência
  }, 3000); // 3 segundos, padrão AddUserGroupForm
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}
// ====================================================================

const groupNameInputRef = ref(null);

// Preenche o formulário e foca no campo ao montar o componente
onMounted(async () => {
  if (props.initialName) {
    formData.value.groupName = props.initialName;
  }

  loadParentGroupOptions();

  await nextTick();
  if (groupNameInputRef.value) {
    groupNameInputRef.value.focus();
  }
  document.addEventListener("keydown", handleEscKey);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEscKey);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
});

// ESC fecha modal
function handleEscKey(event) {
  if (event.key === "Escape" && !isSubmitting.value) {
    closeModal();
  }
}

// Validação e Submissão
function validateForm() {
  errors.value = {};
  if (!formData.value.groupName || formData.value.groupName.trim().length < 3) {
    errors.value.groupName = "O nome do grupo deve ter pelo menos 3 caracteres.";
  }
  return Object.keys(errors.value).length === 0;
}

async function handleSubmit() {
  if (isSubmitting.value) return;

  if (!validateForm()) {
    showErrorToast("Verifique os campos obrigatórios antes de salvar.");
    return;
  }

  isSubmitting.value = true;

  try {
    const payload = {
      groupName: formData.value.groupName,
      description: formData.value.description,
      parentGroupId: formData.value.parentGroupId,
    };

    await groupStore.createGroup(payload);

    // ✅ TOAST DE SUCESSO (usa a função padronizada de 3s)
    showSuccessToast("Grupo criado com sucesso.");
    hideBackdropForSuccess.value = true;

    // ✅ fecha modal depois de 2.4s (padrão AddActivityForm.vue)
    setTimeout(() => {
      emit("refresh");
      emit("close");
      isSubmitting.value = false;
    }, 2400); // Alterado de 4000ms para 2400ms para consistência
  } catch (error) {
    console.error("❌ Erro no formulário de adição de grupo:", error);

    let userFriendlyMessage = error?.message || "Ocorreu um erro ao criar o grupo.";

    if (error?.response?.data?.errors) {
      const messages = Object.values(error.response.data.errors).flat();
      if (messages.length > 0) userFriendlyMessage = messages.join("\n");
    } else if (error?.response?.data?.message) {
      userFriendlyMessage = error.response.data.message;
    }

    // ✅ TOAST DE ERRO (usa a função padronizada de 3s)
    showErrorToast(userFriendlyMessage);
    isSubmitting.value = false;
  }
}

function closeModal() {
  if (!isSubmitting.value) {
    emit("close");
  }
}
</script>

<template>
  <div
    v-show="!hideBackdropForSuccess"
    class="modal-backdrop"
    @click.self="!isSubmitting"
  >
    <div ref="panelRef" class="modal-panel" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h2 class="modal-title">Adicionar Novo Grupo</h2>
        <button
          @click="closeModal"
          class="modal-close-btn"
          :disabled="isSubmitting"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            ></path>
          </svg>
        </button>
      </div>

      <form @submit.prevent="handleSubmit" novalidate>
        <div class="modal-body">
          <div class="form-content">
            <div class="form-group">
              <label for="groupName" class="form-label">Nome do Grupo</label>
              <input
                ref="groupNameInputRef"
                id="groupName"
                type="text"
                v-model="formData.groupName"
                class="input-base"
                :class="{ 'is-invalid': errors.groupName }"
                required
                @focus="$event.target.select()"
                @blur="formData.groupName = toTitleCase(formData.groupName)"
              />
              <span v-if="errors.groupName" class="error-message">
                {{ errors.groupName }}
              </span>
            </div>

            <div class="form-group">
              <label for="parentGroupId" class="form-label">
                Grupo pai (opcional)
              </label>
              <select
                id="parentGroupId"
                v-model="formData.parentGroupId"
                class="input-base"
              >
                <option :value="null">Nenhum - grupo de primeiro nível</option>
                <option
                  v-for="option in parentGroupOptions"
                  :key="option.id"
                  :value="option.id"
                >
                  {{ option.name }}
                </option>
              </select>
              <span class="field-hint">
                Quem estiver neste grupo também recebe o que for concedido ao
                grupo pai.
              </span>
            </div>

            <div class="form-group">
              <label for="description" class="form-label">Descrição (opcional)</label>
              <CustomTextarea
                v-model:body="formData.description"
                placeholder="Digite uma breve descrição (opcional)"
                :is-disabled="false"
                style="min-height: 120px"
              />
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            @click="closeModal"
            class="btn btn-secondary"
            :disabled="isSubmitting"
          >
            Cancelar
          </button>

          <button
            type="submit"
            class="btn btn-success"
            :disabled="isSubmitting"
          >
            <svg
              v-if="isSubmitting"
              class="w-5 h-5 mr-2 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>

            <svg
              v-else
              class="w-5 h-5 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                clip-rule="evenodd"
              ></path>
            </svg>

            <span>{{ isSubmitting ? "Criando..." : "Criar Grupo" }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.modal-panel {
  max-width: 32rem;
}

.form-content {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

select.input-base {
  min-height: 2.6rem;
}

.error-message {
  color: var(--text-important);
  font-size: 0.85rem;
  margin-top: 0.15rem;
}

.field-hint {
  display: block;
  color: var(--text-light);
  font-size: 0.8rem;
  margin-top: 0.2rem;
}

</style>
